// TriggeredTasksList.jsx — m3-16b Agent Jobs LIST (the second Activity tab).
//
// The list screen (design docs/05): header, "Create a new agent job" card (hero button +
// preset carousel), count line, toggled rows with edit affordance.
// m3-16b wires the three entry points (New button, preset card, edit pencil) to the builder.
//
// NAMING (LOCKED per TRIGGERED-SPEC-v2 §0):
//   Tab label: "Agent Jobs" (activityTabsViewModel.js)
//   Page H1:   "Agent Jobs"
//   Card H2:   "Create a new agent job"
//   Button:    "New agent job"
//
// DESIGN-MATCH (TRIGGERED-SPEC, 2026-06-18):
//   TD-1: No icon tile on preset cards (.ag-sg-ic stays removed).
//   TD-2: No PRE-CONFIGURED badge (.ag-sg-tag stays removed).
//   TD-3: Vertical compression (padding/gap/head removal) — ~55–65px saved.
//   Glyphs replaced with ShellIcon SVG; scope labels hidden; structured count line.
//   PresetCard is a <button>; condition pill + channel-restriction footer tag rendered.

import React, { useMemo, useRef, useState, useCallback, useEffect, useId, useLayoutEffect } from "react"
import ShellIcon from "../shell/icons.jsx"
import NewAgentForm from "./NewAgentForm.jsx"
import {
  buildList, toggleTask, buildPresets, carouselArrows, KIND_ICON,
  apiJobToTask, AGENT_TRIGGERS_GATE, GATE_BLOCKED_COPY, buildReviewSeedPrompt,
  buildStandingApproval,
} from "./triggeredListViewModel.js"
// H-GATE (spec-H): the list is REAL-API-backed (GET/PATCH /api/agent/companies/:slug/agent_jobs),
// gated on the coarse `agent-triggers` FeatureGate. SEED_AUTOMATIONS is no longer imported — a
// non-gated company (or a 403) gets a neutral empty state, NEVER the demo seed rows.
import { fetchData, patchData, postData, deleteData } from "../utils/api"
import useFeatureGates from "../hooks/useFeatureGates"
import { buildCreateChatBody } from "./createChatBody.js"
import { chatCreatedEvent } from "./navChatList.js"
// Item #7: agent-mode View-all keeps the agent chrome (?mode=agent) and stages a RETURN descriptor
// ({chatId from the AgentStudioChat sessionStorage mirror, activityView}) so the library's back
// button can restore the exact shell state. Standard mode keeps the plain href, stages nothing.
import { agentLibraryHref, readActiveChatId, stageReturn } from "./libraryReturn.js"

// The company slug is stamped on the react-root mount node by the Rails layout (the same source
// packs/react_app.js reads for the router). Read it directly — ActivitySurface renders this list
// without props, and the slug never changes within a mount.
function readCompanySlug() {
  try {
    const node = document.getElementById("react-root")
    return node ? node.getAttribute("company-slug") : null
  } catch (_e) {
    return null
  }
}

function Toggle({ on, onClick, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`ag-switch ${on ? "is-on" : ""}`}
      onClick={onClick}
    >
      <span className="ag-switch__knob" />
    </button>
  )
}

// B8 — the "Standing approval" panel (design §7). A standing approval is durable money consent:
// one card, taken once, that lets future qualifying events be added to one campaign with no
// per-event approval. The panel is how a customer SEES what they agreed to and turns it off.
//
// Presentational only — every string comes from buildStandingApproval (pure, node-testable).
// The Revoke button is `.pgs-decline` (the repo's destructive-secondary class); a bare <button>
// would render as the gray Win95 default, which is never acceptable here.
function StandingApprovalPanel({ standing, onRevoke }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const revoke = () => {
    setSubmitting(true)
    setError(null)
    Promise.resolve(onRevoke(standing.id))
      .catch(() => setError("Couldn't revoke that just now — try again."))
      .then(() => setSubmitting(false))
  }

  return (
    <div className={`ag-sa${standing.live ? " is-live" : ""}`}>
      <div className="ag-sa-head">
        <span className="ag-sa-title">Standing approval</span>
        <span className="ag-sa-state">{standing.stateLabel}</span>
      </div>
      <dl className="ag-sa-facts">
        <div><dt>Campaign</dt><dd>{standing.campaignLabel}</dd></div>
        {standing.budgetLabel && <div><dt>Budget</dt><dd>{standing.budgetLabel}</dd></div>}
        <div><dt>Activity</dt><dd>{standing.runsLabel}</dd></div>
        {standing.grantedLabel && <div><dt>Approved</dt><dd>{standing.grantedLabel}</dd></div>}
        {standing.expiryLabel && <div><dt>Expires</dt><dd>{standing.expiryLabel}</dd></div>}
      </dl>
      <p className="ag-sa-activation">{standing.activationLabel}</p>
      {standing.note && <p className="ag-sa-note">{standing.note}</p>}
      {error && <p className="ag-sa-note ag-sa-note--err">{error}</p>}
      {standing.canRevoke && (
        <div className="ag-sa-actions">
          <button type="button" className="pgs-decline" disabled={submitting} onClick={revoke}>
            {submitting ? "Revoking…" : "Revoke"}
          </button>
        </div>
      )}
    </div>
  )
}

function AgentRow({ row, onToggle, onEdit, onRevokeStanding }) {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [descriptionOverflows, setDescriptionOverflows] = useState(false)
  const descriptionId = useId()
  const descriptionRef = useRef(null)
  const action = row.action || ""
  const standing = useMemo(() => buildStandingApproval(row.standingApproval), [row.standingApproval])

  // Measure the actual clamped box instead of guessing from character count: the same
  // description may fit at full-page width and overflow in the narrower resizable panel.
  // ResizeObserver rechecks whenever that rendered width changes.
  useLayoutEffect(() => {
    if (descriptionExpanded) return undefined
    const description = descriptionRef.current
    if (!description) return undefined

    const measureOverflow = () => {
      setDescriptionOverflows(description.scrollHeight > description.clientHeight + 1)
    }
    measureOverflow()

    if (typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(measureOverflow)
    observer.observe(description)
    return () => observer.disconnect()
  }, [action, descriptionExpanded])

  return (
    <div className={`ag-row ${row.enabled ? "" : "off"} ${row.isNew ? "isnew" : ""}`}>
      <div className="ag-glyph" aria-hidden="true">
        <ShellIcon name={row.icon || "sparkle"} size={19} />
      </div>
      <div className="ag-main">
        <div className="ag-name">
          {row.name}
          {row.isNew && <span className="ag-newtag">New</span>}
        </div>
        <div
          id={descriptionId}
          ref={descriptionRef}
          className={`ag-desc${descriptionExpanded ? " is-expanded" : ""}`}
        >
          {action}
        </div>
        {(descriptionOverflows || descriptionExpanded) && (
          <button
            type="button"
            className="ag-desc-toggle"
            aria-expanded={descriptionExpanded}
            aria-controls={descriptionId}
            onClick={() => setDescriptionExpanded((expanded) => !expanded)}
          >
            {descriptionExpanded ? "Hide details" : "Show details"}
          </button>
        )}
        <div className="ag-when">
          <span className="ag-kind-ico" aria-hidden="true">
            <ShellIcon name={row.kindIcon} size={12} />
          </span>
          {row.trigger}
          {/* scope labels intentionally not rendered (TRIGGERED-SPEC TD-5, AC#9) */}
        </div>
        {/* B8: absent for every job that has never had a standing approval — which is every job
            until the agent-standing-auth gate is turned on for a company. */}
        {standing && (
          <StandingApprovalPanel
            standing={standing}
            onRevoke={(authId) => onRevokeStanding(row.id, authId)}
          />
        )}
      </div>
      <div className="ag-actions">
        <button
          type="button"
          className="ag-edit"
          aria-label={`Edit ${row.name}`}
          onClick={() => onEdit(row)}
        >
          <ShellIcon name="pen" size={15} />
        </button>
        <Toggle
          on={row.enabled}
          onClick={() => onToggle(row.id)}
          label={(row.enabled ? "Pause " : "Turn on ") + row.name}
        />
      </div>
    </div>
  )
}

// TD-1: No icon tile (.ag-sg-ic removed — LOCKED).
// TD-2: No PRE-CONFIGURED badge (.ag-sg-tag removed — LOCKED).
// Renders condition pill (for condition-kind presets) + channel-restriction footer tag from note.
function PresetCard({ preset, onPick }) {
  return (
    <button
      type="button"
      className="ag-sg"
      onClick={() => onPick(preset)}
    >
      <div className="ag-sg-name">{preset.name}</div>
      <div className="ag-sg-short">{preset.short}</div>
      {/* Condition pill — visible for condition-kind presets (TRIGGERED-SPEC-v2 §T-P2) */}
      {preset.kind === "condition" && (
        <div className="ag-sg-when">
          <span className="ag-kind-ico" aria-hidden="true">
            <ShellIcon name={KIND_ICON["condition"]} size={12} />
          </span>
          {preset.trigger}
        </div>
      )}
      {/* Schedule/event trigger line for non-condition presets */}
      {preset.kind !== "condition" && (
        <div className="ag-sg-when">
          <span className="ag-kind-ico" aria-hidden="true">
            <ShellIcon name={preset.kindIcon} size={12} />
          </span>
          {preset.trigger}
        </div>
      )}
      <div className="ag-sg-foot">
        {/* Channel-restriction footer tag from note field */}
        {preset.note && <span className="ag-sg-note">{preset.note}</span>}
        <span className="ag-sg-add">
          <ShellIcon name="plus" size={13} />
          Use preset
        </span>
      </div>
    </button>
  )
}

export default function TriggeredTasksList({ seedJob = null, agentMode = false }) {
  // H-GATE: API-backed state — starts EMPTY (never SEED_AUTOMATIONS). jobsState drives the shell:
  //   'loading' → header only; 'ready' → the full list; 'blocked' → the neutral not-enabled state
  //   (gate absent or the server 403'd); 'error' → a neutral retry note (non-403 failure).
  const companySlug = useMemo(() => readCompanySlug(), [])
  // Task 9 D-T9-h / item #7: entry point into the full library from the preset carousel. In agent
  // mode (panel placement) the href carries ?mode=agent so the library keeps the agent side nav;
  // Standard mode keeps the plain form (a Standard user must never be flipped into agent chrome).
  const libraryHref = agentMode
    ? agentLibraryHref(companySlug)
    : (companySlug ? `/companies/${companySlug}/library` : "/library")
  // Item #7: at click time stage the RETURN descriptor for the library back button. This list IS
  // the Agent Jobs view, so activityView is definitionally 'agents'; the open chat id (or null when
  // launched from Board with no chat — impossible here, but the mirror decides) comes from the
  // AgentStudioChat sessionStorage mirror. Navigation proceeds via the anchor's own href.
  const onViewAllClick = useCallback(() => {
    if (!agentMode) return
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    stageReturn(store, { chatId: readActiveChatId(store), activityView: "agents" })
  }, [agentMode])
  const { featureGates, gatesLoading } = useFeatureGates("company")
  const gated = featureGates.includes(AGENT_TRIGGERS_GATE)
  const [tasks, setTasks] = useState([])
  const [jobsState, setJobsState] = useState("loading")
  const [creating, setCreating] = useState(false)
  const [editSeed, setEditSeed] = useState(null)  // task being edited, or a suggestion seed
  const [formMode, setFormMode] = useState("create") // 'create' | 'edit'

  const carRef = useRef(null)
  const [arrows, setArrows] = useState({ prevDisabled: true, nextDisabled: false })
  const dragState = useRef({ down: false, startX: 0, startScroll: 0, moved: false })

  const list = useMemo(() => buildList(tasks), [tasks])
  const presets = useMemo(() => buildPresets(), [])

  // Fetch the REAL jobs once the gates have resolved. The server-side require_agent_jobs_gate! is
  // the load-bearing check — a 403 here (or a missing gate client-side) degrades to 'blocked'.
  useEffect(() => {
    if (gatesLoading) return undefined
    if (!gated || !companySlug) {
      setJobsState("blocked")
      setTasks([])
      return undefined
    }
    let cancelled = false
    setJobsState("loading")
    fetchData(`api/agent/companies/${companySlug}/agent_jobs`)
      .then((rows) => {
        if (cancelled) return
        setTasks((Array.isArray(rows) ? rows : []).map(apiJobToTask))
        setJobsState("ready")
      })
      .catch((err) => {
        if (cancelled) return
        const status = err && err.response && err.response.status
        setTasks([])
        setJobsState(status === 403 ? "blocked" : "error")
      })
    return () => { cancelled = true }
  }, [gatesLoading, gated, companySlug])

  // H3: the toggle PERSISTS via PATCH { agent_job: { enabled } } — the row's `enabled` column is
  // what the tick worker filters on, so this provably starts/stops firing. Optimistic flip; revert
  // on failure (never leave the UI claiming a state the server refused).
  const onToggle = useCallback((id) => {
    const row = tasks.find((t) => t.id === id)
    if (!row || !companySlug) return
    const next = !row.enabled
    setTasks((prev) => toggleTask(prev, id))
    patchData(`api/agent/companies/${companySlug}/agent_jobs/${id}`, { agent_job: { enabled: next } })
      .catch(() => setTasks((prev) => toggleTask(prev, id)))
  }, [tasks, companySlug])

  // B8 — REVOKE a standing approval (DELETE /api/agent/standing_authorizations/:id). Deliberately
  // NOT optimistic: this is durable money consent, so the panel only changes once the server has
  // confirmed, and it re-renders from the row the server returned (never a locally-invented state).
  // The endpoint is idempotent, so a double-click is harmless.
  const onRevokeStanding = useCallback((jobId, authId) => (
    deleteData(`api/agent/standing_authorizations/${authId}`).then((res) => {
      const next = (res && res.data) || null
      setTasks((prev) => prev.map((t) => (t.id === jobId ? { ...t, standingApproval: next } : t)))
    })
  ), [])

  const updateArrows = useCallback(() => {
    const el = carRef.current
    if (el) setArrows(carouselArrows(el))
  }, [])

  const scrollBy = (dx) => {
    const el = carRef.current
    if (el) el.scrollBy({ left: dx, behavior: "smooth" })
  }

  const onCarPointerDown = (e) => {
    const el = carRef.current
    if (!el) return
    dragState.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false }
  }
  const onCarPointerMove = (e) => {
    const st = dragState.current
    const el = carRef.current
    if (!st.down || !el) return
    const dx = e.clientX - st.startX
    if (Math.abs(dx) > 4) st.moved = true
    el.scrollLeft = st.startScroll - dx
  }
  const onCarPointerUp = () => { dragState.current.down = false }
  const onCarClickCapture = (e) => {
    if (dragState.current.moved) {
      e.preventDefault()
      e.stopPropagation()
      dragState.current.moved = false
    }
  }

  // ── Entry points (TRIGGERED-SPEC-v2 §T-P0-A) ─────────────────────────────────────────
  const openNew        = () => { setEditSeed(null);   setFormMode("create"); setCreating(true) }
  const openEdit       = (task) => { setEditSeed(task);  setFormMode("edit");   setCreating(true) }
  // Task 9: a slider preset carries its builder seed on `librarySeed` (library-trigger mode); a
  // plain suggestion seeds directly. The cross-page launch passes a bare jobSeed as `seedJob`.
  const openSuggestion = (sug)  => { setEditSeed(sug && sug.librarySeed ? sug.librarySeed : sug); setFormMode("create"); setCreating(true) }
  const closeForm      = () => { setCreating(false); setEditSeed(null) }

  // Task 9 D-T9-g: the cross-page library→builder launch. When the shell hands us a seedJob (from a
  // consumed launch descriptor), open NewAgentForm prefilled in library-trigger mode — exactly once.
  const seededRef = useRef(false)
  useEffect(() => {
    if (seedJob && !seededRef.current) {
      seededRef.current = true
      openSuggestion(seedJob)
    }
  }, [seedJob]) // eslint-disable-line react-hooks/exhaustive-deps

  // D-wire: seed a review chat right after a real job create. Best-effort, non-blocking, never
  // gates the job on a reply — a failure here (chat gate off, network) is swallowed with only a
  // console.warn (failure matrix: job row stays, no scary UI).
  const seedReviewChat = async (job) => {
    try {
      const messageId = crypto.randomUUID()
      const text = buildReviewSeedPrompt(job)
      // review_job_id: the durable link that lets THIS chat's update_agent_job edit THIS job even
      // though it is already switched on (chats#create → Session#review_agent_job_id).
      const body = { ...buildCreateChatBody({ text, messageId }), review_job_id: job.id }
      const created = await postData(`api/agent/companies/${companySlug}/chats`, body)
      window.dispatchEvent(new CustomEvent("vesta:nav:chat-created",
        { detail: chatCreatedEvent(created.id, "New job review") }))
      // freshRun: this chat was created with a run already in flight (the seed prompt POSTed
      // above), so AgentStudioChat must arm its thinking indicator on adoption — selecting a chat
      // normally RESETS sentSinceTerminal, which is why seeded review chats showed no "Thinking…".
      // seedText/seedMessageId (E-thinking-verbs, template-form variant): freshRun alone arms the
      // isWorking() predicate but does NOT make the thread non-empty — AgentStudioChat's thread
      // (and the {working && ThinkingIndicator} nested inside it) stays hidden behind the empty
      // hero until the durable echo replays over SessionChannel, since this seed message was
      // POSTed directly here, never pushed onto AgentStudioChat's own local userTurns. Carrying the
      // verbatim text + the SAME message_id just POSTed lets AgentStudioChat plant it as an
      // optimistic turn immediately (mirroring sendText's own arm) — convo.isEmpty flips false on
      // this same tick, and the message_id match lets the durable echo dedup it cleanly later.
      window.dispatchEvent(new CustomEvent("vesta:nav:select-chat",
        { detail: { id: String(created.id), freshRun: true, seedText: text, seedMessageId: messageId } }))
    } catch (e) {
      if (window.console) console.warn("review chat seed skipped", e)
    }
  }

  // D-wire: NewAgentForm's create path really POSTs to AgentJobsController#create, so
  // submitAgent receives the serialized AgentJob row (create.rb#serialize shape) — real jobs are
  // ALWAYS shown first (D-order), then a review chat is seeded detached/non-blocking.
  //
  // [AMENDED-BY-ORCH D9+D10, iteration 3] Edit-mode branch: NewAgentForm now really PATCHes
  // AgentJobsController#update, so `job` here is ALSO a create.rb#serialize shape (not the old
  // locally-built task object) — it MUST go through apiJobToTask same as create. Unlike create,
  // edit updates the MATCHING row IN PLACE (rows +0, no prepend) and never seeds a review chat.
  const submitAgent = (job) => {
    if (formMode === "edit") {
      const updated = { ...apiJobToTask(job), isNew: false }
      setTasks((xs) => xs.map((a) => (a.id === updated.id ? updated : a)))
      closeForm()
      return
    }
    setTasks((xs) => [
      { ...apiJobToTask(job), isNew: true },
      ...xs.map((a) => ({ ...a, isNew: false })),
    ])
    closeForm()
    seedReviewChat(job)
  }

  // H-GATE: neutral degraded states — header + a quiet note. NEVER the seed rows, never a crash,
  // and no create/builder surface for a company that isn't gated.
  if (jobsState === "blocked") {
    return (
      <div className="ag ag-wrap">
        <div className="ag-head">
          <h1 className="ag-h1">Agent Jobs</h1>
          <p className="ag-sub">{GATE_BLOCKED_COPY}</p>
        </div>
      </div>
    )
  }
  if (jobsState === "error") {
    return (
      <div className="ag ag-wrap">
        <div className="ag-head">
          <h1 className="ag-h1">Agent Jobs</h1>
          <p className="ag-sub">Couldn&apos;t load agent jobs. Refresh to try again.</p>
        </div>
      </div>
    )
  }
  if (jobsState === "loading") {
    return (
      <div className="ag ag-wrap">
        <div className="ag-head">
          <h1 className="ag-h1">Agent Jobs</h1>
          <p className="ag-sub" role="status">Loading…</p>
        </div>
      </div>
    )
  }

  // Builder replaces the list (full-pane swap, not a modal)
  if (creating) {
    return (
      <NewAgentForm
        seed={editSeed}
        mode={formMode}
        onCancel={closeForm}
        onCreate={submitAgent}
        companySlug={companySlug}
      />
    )
  }

  return (
    <div className="ag ag-wrap">
      <div className="ag-head">
        <h1 className="ag-h1">Agent Jobs</h1>
        <p className="ag-sub">
          Set up recurring work once, and Vesta handles it automatically.
        </p>
        <p className="ag-sub">
          Social media post generation now lives here. The old Content Plan and Generate Posts
          settings in Social Media have been retired.
        </p>
      </div>

      {/* Create a new agent job — primary action up top, presets as a "by the way" */}
      <div className="ag-create">
        <div className="ag-create-hero">
          <div>
            <h2 className="ag-create-h2">Create a new agent job</h2>
            <p className="ag-create-p">
              Choose what Vesta should do and when it should run.
            </p>
          </div>
          <button
            type="button"
            className="ag-new ag-new-lg"
            onClick={openNew}
          >
            <ShellIcon name="plus" size={17} />
            New agent job
          </button>
        </div>

        <div className="ag-create-sug">
          <div className="ag-template-head">
            <h3 className="ag-template-title">Start with a template</h3>
            <a className="ag-view-all" href={libraryHref} onClick={onViewAllClick}>View all →</a>
          </div>
          <div
            className="ag-car"
            ref={carRef}
            onScroll={updateArrows}
            onPointerDown={onCarPointerDown}
            onPointerMove={onCarPointerMove}
            onPointerUp={onCarPointerUp}
            onPointerLeave={onCarPointerUp}
            onClickCapture={onCarClickCapture}
          >
            {presets.map((p) => (
              <PresetCard key={p.id} preset={p} onPick={openSuggestion} />
            ))}
          </div>
          <div className="ag-car-nav">
            <button
              type="button"
              className="ag-car-btn"
              disabled={arrows.prevDisabled}
              onClick={() => scrollBy(-272)}
              aria-label="Previous presets"
            >
              {/* Rotate span not the button (TRIGGERED-SPEC-v2 §T-P2) */}
              <span style={{ display: "grid", transform: "rotate(180deg)" }}>
                <ShellIcon name="chevron-right" size={16} />
              </span>
            </button>
            <button
              type="button"
              className="ag-car-btn"
              disabled={arrows.nextDisabled}
              onClick={() => scrollBy(272)}
              aria-label="More presets"
            >
              <ShellIcon name="chevron-right" size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* the list — count line moved inside as a header */}
      <div className="ag-list">
        <div className="ag-list-head">
          <h2 className="ag-list-title">Your agent jobs</h2>
          <div className="ag-meta">
            <span className="ag-meta-dot" />
            <span><b>{list.running}</b> running</span>
            <span className="ag-meta-sep" />
            <span><b>{list.paused}</b> paused</span>
          </div>
        </div>
        {list.rows.map((row) => (
          <AgentRow
            key={row.id}
            row={row}
            onToggle={onToggle}
            onEdit={openEdit}
            onRevokeStanding={onRevokeStanding}
          />
        ))}
      </div>
    </div>
  )
}
